/**
 * Cash-Flow Simulator
 * Real-time calculation of rental investment profitability
 */

document.addEventListener('DOMContentLoaded', function () {
    // =============================================
    // CONSTANTS & CONFIGURATION
    // =============================================
    const DEFAULT_VALUES = {
        purchasePrice: 150000,
        notaryFeesRate: 8,
        worksAmount: 0,
        downPayment: 0,
        interestRate: 3.5,
        loanDuration: 20,
        insuranceRate: 0.3,
        monthlyRent: 700,
        vacancyRate: 8,
        propertyTax: 800,
        condoFees: 400,
        insurancePno: 150,
        managementFeesRate: 0,
        maintenanceRate: 4
    };

    // =============================================
    // DOM ELEMENTS
    // =============================================
    const elements = {
        // Acquisition
        purchasePrice: document.getElementById('purchase-price'),
        notaryFeesRate: document.getElementById('notary-fees-rate'),
        notaryFeesAmount: document.getElementById('notary-fees-amount'),
        worksAmount: document.getElementById('works-amount'),
        totalInvestment: document.getElementById('total-investment'),

        // Financing
        downPayment: document.getElementById('down-payment'),
        loanAmount: document.getElementById('loan-amount'),
        interestRate: document.getElementById('interest-rate'),
        loanDuration: document.getElementById('loan-duration'),
        insuranceRate: document.getElementById('insurance-rate'),
        insuranceAmount: document.getElementById('insurance-amount'),
        monthlyPayment: document.getElementById('monthly-payment'),

        // Income
        monthlyRent: document.getElementById('monthly-rent'),
        annualRent: document.getElementById('annual-rent'),
        vacancyRate: document.getElementById('vacancy-rate'),
        vacancyAmount: document.getElementById('vacancy-amount'),
        netAnnualRent: document.getElementById('net-annual-rent'),

        // Charges
        propertyTax: document.getElementById('property-tax'),
        condoFees: document.getElementById('condo-fees'),
        insurancePno: document.getElementById('insurance-pno'),
        managementFeesRate: document.getElementById('management-fees-rate'),
        managementFeesAmount: document.getElementById('management-fees-amount'),
        maintenanceRate: document.getElementById('maintenance-rate'),
        maintenanceAmount: document.getElementById('maintenance-amount'),
        totalCharges: document.getElementById('total-charges'),

        // Results
        resultGrossYield: document.getElementById('result-gross-yield'),
        resultNetYield: document.getElementById('result-net-yield'),
        resultCashflow: document.getElementById('result-cashflow'),
        resultCashflowCard: document.getElementById('result-cashflow-card'),
        resultEffort: document.getElementById('result-effort'),

        // Sidebar
        sidebarTotalInvestment: document.getElementById('sidebar-total-investment'),
        sidebarAnnualRent: document.getElementById('sidebar-annual-rent'),
        sidebarCashflow: document.getElementById('sidebar-cashflow'),

        // Detail table
        detailRent: document.getElementById('detail-rent'),
        detailMortgage: document.getElementById('detail-mortgage'),
        detailTax: document.getElementById('detail-tax'),
        detailCondo: document.getElementById('detail-condo'),
        detailPno: document.getElementById('detail-pno'),
        detailManagement: document.getElementById('detail-management'),
        detailMaintenance: document.getElementById('detail-maintenance'),
        detailVacancy: document.getElementById('detail-vacancy'),
        detailCashflow: document.getElementById('detail-cashflow'),
        detailTotalRow: document.getElementById('detail-total-row'),

        // Advice
        cashflowAdvice: document.getElementById('cashflow-advice'),

        // Actions
        resetForm: document.getElementById('reset-form')
    };

    // =============================================
    // UTILITY FUNCTIONS
    // =============================================

    /**
     * Format a number as currency (French format)
     */
    function formatCurrency(value, showSign = false) {
        const formatted = new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.abs(value));

        if (showSign) {
            return value >= 0 ? `+ ${formatted} €` : `- ${formatted} €`;
        }
        return `${formatted} €`;
    }

    /**
     * Format a number as percentage
     */
    function formatPercent(value) {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value) + ' %';
    }

    /**
     * Parse a formatted currency string to number
     */
    function parseCurrency(value) {
        if (typeof value === 'number') return value;
        // Remove spaces, currency symbols, and replace comma with dot
        const cleaned = String(value).replace(/[^\d,.-]/g, '').replace(',', '.');
        return parseFloat(cleaned) || 0;
    }

    /**
     * Parse a formatted percentage string to number
     */
    function parsePercent(value) {
        if (typeof value === 'number') return value;
        const cleaned = String(value).replace(/[^\d,.-]/g, '').replace(',', '.');
        return parseFloat(cleaned) || 0;
    }

    /**
     * Format input value on blur
     */
    function formatInputValue(input, isCurrency = true) {
        const value = isCurrency ? parseCurrency(input.value) : parsePercent(input.value);
        if (isCurrency) {
            input.value = new Intl.NumberFormat('fr-FR').format(value);
        } else {
            input.value = value.toString().replace('.', ',');
        }
    }

    /**
     * Calculate monthly loan payment (annuity formula)
     */
    function calculateMonthlyPayment(principal, annualRate, durationYears) {
        if (principal <= 0 || durationYears <= 0) return 0;
        if (annualRate <= 0) return principal / (durationYears * 12);

        const monthlyRate = annualRate / 100 / 12;
        const numberOfPayments = durationYears * 12;

        return principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
            (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    }

    // =============================================
    // CALCULATION ENGINE
    // =============================================

    function calculate() {
        // Get all values
        const purchasePrice = parseCurrency(elements.purchasePrice.value);
        const notaryFeesRate = parsePercent(elements.notaryFeesRate.value);
        const worksAmount = parseCurrency(elements.worksAmount.value);
        const downPayment = parseCurrency(elements.downPayment.value);
        const interestRate = parsePercent(elements.interestRate.value);
        const loanDuration = parseInt(elements.loanDuration.value) || 20;
        const insuranceRate = parsePercent(elements.insuranceRate.value);
        const monthlyRent = parseCurrency(elements.monthlyRent.value);
        const vacancyRate = parsePercent(elements.vacancyRate.value);
        const propertyTax = parseCurrency(elements.propertyTax.value);
        const condoFees = parseCurrency(elements.condoFees.value);
        const insurancePno = parseCurrency(elements.insurancePno.value);
        const managementFeesRate = parsePercent(elements.managementFeesRate.value);
        const maintenanceRate = parsePercent(elements.maintenanceRate.value);

        // Calculate acquisition
        const notaryFeesAmount = purchasePrice * notaryFeesRate / 100;
        const totalInvestment = purchasePrice + notaryFeesAmount + worksAmount;

        // Calculate financing
        const loanAmount = Math.max(0, totalInvestment - downPayment);
        const monthlyLoanPayment = calculateMonthlyPayment(loanAmount, interestRate, loanDuration);
        const monthlyInsurance = (loanAmount * insuranceRate / 100) / 12;
        const totalMonthlyPayment = monthlyLoanPayment + monthlyInsurance;

        // Calculate income
        const annualRent = monthlyRent * 12;
        const vacancyAmount = annualRent * vacancyRate / 100;
        const netAnnualRent = annualRent - vacancyAmount;

        // Calculate charges
        const managementFeesAmount = annualRent * managementFeesRate / 100;
        const maintenanceAmount = annualRent * maintenanceRate / 100;
        const totalCharges = propertyTax + condoFees + insurancePno + managementFeesAmount + maintenanceAmount;

        // Calculate yields
        const grossYield = totalInvestment > 0 ? (annualRent / totalInvestment) * 100 : 0;
        const netYield = totalInvestment > 0 ? ((netAnnualRent - totalCharges) / totalInvestment) * 100 : 0;

        // Calculate cash-flow
        const monthlyCharges = totalCharges / 12;
        const monthlyVacancy = vacancyAmount / 12;
        const cashflow = monthlyRent - totalMonthlyPayment - monthlyCharges - monthlyVacancy;
        const effortEpargne = cashflow < 0 ? Math.abs(cashflow) : 0;

        // Update display - Acquisition
        elements.notaryFeesAmount.textContent = new Intl.NumberFormat('fr-FR').format(Math.round(notaryFeesAmount));
        elements.totalInvestment.textContent = formatCurrency(Math.round(totalInvestment));

        // Update display - Financing
        elements.loanAmount.value = new Intl.NumberFormat('fr-FR').format(Math.round(loanAmount));
        elements.insuranceAmount.textContent = new Intl.NumberFormat('fr-FR').format(Math.round(monthlyInsurance));
        elements.monthlyPayment.textContent = formatCurrency(Math.round(totalMonthlyPayment)) + '/mois';

        // Update display - Income
        elements.annualRent.textContent = new Intl.NumberFormat('fr-FR').format(Math.round(annualRent));
        elements.vacancyAmount.textContent = new Intl.NumberFormat('fr-FR').format(Math.round(vacancyAmount));
        elements.netAnnualRent.textContent = formatCurrency(Math.round(netAnnualRent)) + '/an';

        // Update display - Charges
        elements.managementFeesAmount.textContent = new Intl.NumberFormat('fr-FR').format(Math.round(managementFeesAmount));
        elements.maintenanceAmount.textContent = new Intl.NumberFormat('fr-FR').format(Math.round(maintenanceAmount));
        elements.totalCharges.textContent = formatCurrency(Math.round(totalCharges)) + '/an';

        // Update display - Results cards
        elements.resultGrossYield.textContent = formatPercent(grossYield);
        elements.resultNetYield.textContent = formatPercent(netYield);
        elements.resultCashflow.textContent = formatCurrency(Math.round(cashflow));
        elements.resultEffort.textContent = effortEpargne > 0 ? formatCurrency(Math.round(effortEpargne)) + '/mois' : '0 €/mois';

        // Update cashflow card color
        elements.resultCashflowCard.classList.remove('positive', 'negative');
        elements.resultCashflowCard.classList.add(cashflow >= 0 ? 'positive' : 'negative');

        // Update sidebar
        elements.sidebarTotalInvestment.textContent = formatCurrency(Math.round(totalInvestment));
        elements.sidebarAnnualRent.textContent = formatCurrency(Math.round(annualRent));
        elements.sidebarCashflow.textContent = formatCurrency(Math.round(cashflow));
        elements.sidebarCashflow.classList.remove('positive', 'negative');
        elements.sidebarCashflow.classList.add(cashflow >= 0 ? 'positive' : 'negative');

        // Update detail table
        elements.detailRent.textContent = `+ ${new Intl.NumberFormat('fr-FR').format(Math.round(monthlyRent))} €`;
        elements.detailMortgage.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(totalMonthlyPayment))} €`;
        elements.detailTax.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(propertyTax / 12))} €`;
        elements.detailCondo.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(condoFees / 12))} €`;
        elements.detailPno.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(insurancePno / 12))} €`;
        elements.detailManagement.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(managementFeesAmount / 12))} €`;
        elements.detailMaintenance.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(maintenanceAmount / 12))} €`;
        elements.detailVacancy.textContent = `- ${new Intl.NumberFormat('fr-FR').format(Math.round(monthlyVacancy))} €`;

        const cashflowFormatted = cashflow >= 0
            ? `+ ${new Intl.NumberFormat('fr-FR').format(Math.round(cashflow))} €`
            : `- ${new Intl.NumberFormat('fr-FR').format(Math.round(Math.abs(cashflow)))} €`;
        elements.detailCashflow.innerHTML = `<strong>${cashflowFormatted}</strong>`;

        elements.detailTotalRow.classList.remove('positive', 'negative');
        elements.detailTotalRow.classList.add(cashflow >= 0 ? 'positive' : 'negative');

        // Update advice
        updateAdvice(cashflow, grossYield, netYield, effortEpargne);
    }

    /**
     * Update the advice text based on results
     */
    function updateAdvice(cashflow, grossYield, netYield, effortEpargne) {
        let advice = '';

        if (cashflow >= 100) {
            advice = `Excellent ! Votre cash-flow est positif de ${formatCurrency(Math.round(cashflow))}/mois. Ce bien s'autofinance et vous rapporte de l'argent chaque mois. C'est un investissement rentable.`;
        } else if (cashflow >= 0) {
            advice = `Bien ! Votre cash-flow est à l'équilibre. Le bien s'autofinance, mais ne génère pas de revenus complémentaires. C'est un investissement patrimonial correct.`;
        } else if (effortEpargne <= 150) {
            advice = `Votre cash-flow est légèrement négatif. Vous devrez sortir ${formatCurrency(Math.round(effortEpargne))}/mois de votre poche. Cet effort d'épargne reste raisonnable si vous avez une bonne capacité d'épargne.`;
        } else if (effortEpargne <= 300) {
            advice = `Attention : votre effort d'épargne est de ${formatCurrency(Math.round(effortEpargne))}/mois. Assurez-vous que cette somme représente moins de 30% de votre capacité d'épargne mensuelle.`;
        } else {
            advice = `Vigilance : votre effort d'épargne de ${formatCurrency(Math.round(effortEpargne))}/mois est élevé. Envisagez d'augmenter votre apport, d'allonger la durée du prêt, ou de négocier le prix d'achat.`;
        }

        // Add yield commentary
        if (grossYield < 4) {
            advice += ` La rentabilité brute de ${formatPercent(grossYield)} est faible, ce qui est typique des zones tendues à fort potentiel de plus-value.`;
        } else if (grossYield >= 7) {
            advice += ` La rentabilité brute de ${formatPercent(grossYield)} est élevée, mais vérifiez le risque locatif et le potentiel de valorisation.`;
        }

        elements.cashflowAdvice.textContent = advice;
    }

    // =============================================
    // EVENT LISTENERS
    // =============================================

    // Currency inputs
    document.querySelectorAll('.currency-input').forEach(input => {
        input.addEventListener('input', function () {
            // Remove non-numeric characters except spaces
            this.value = this.value.replace(/[^\d\s]/g, '');
            calculate();
        });

        input.addEventListener('blur', function () {
            formatInputValue(this, true);
        });

        input.addEventListener('focus', function () {
            // Remove formatting on focus
            this.value = parseCurrency(this.value).toString();
            this.select();
        });
    });

    // Percentage inputs
    document.querySelectorAll('.percent-input').forEach(input => {
        input.addEventListener('input', function () {
            // Allow digits, comma, and dot
            this.value = this.value.replace(/[^\d,.\s]/g, '');
            calculate();
        });

        input.addEventListener('blur', function () {
            formatInputValue(this, false);
        });

        input.addEventListener('focus', function () {
            this.select();
        });
    });

    // Number input (duration)
    elements.loanDuration.addEventListener('input', calculate);
    elements.loanDuration.addEventListener('change', function () {
        if (this.value < 5) this.value = 5;
        if (this.value > 30) this.value = 30;
        calculate();
    });

    // Down payment affects loan amount
    elements.downPayment.addEventListener('input', function () {
        const totalInvestment = parseCurrency(elements.purchasePrice.value) +
            (parseCurrency(elements.purchasePrice.value) * parsePercent(elements.notaryFeesRate.value) / 100) +
            parseCurrency(elements.worksAmount.value);
        const downPayment = parseCurrency(this.value);

        if (downPayment > totalInvestment) {
            this.value = new Intl.NumberFormat('fr-FR').format(totalInvestment);
        }
        calculate();
    });

    // Reset button
    elements.resetForm.addEventListener('click', function () {
        // Reset all values to defaults
        elements.purchasePrice.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.purchasePrice);
        elements.notaryFeesRate.value = DEFAULT_VALUES.notaryFeesRate.toString().replace('.', ',');
        elements.worksAmount.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.worksAmount);
        elements.downPayment.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.downPayment);
        elements.interestRate.value = DEFAULT_VALUES.interestRate.toString().replace('.', ',');
        elements.loanDuration.value = DEFAULT_VALUES.loanDuration;
        elements.insuranceRate.value = DEFAULT_VALUES.insuranceRate.toString().replace('.', ',');
        elements.monthlyRent.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.monthlyRent);
        elements.vacancyRate.value = DEFAULT_VALUES.vacancyRate.toString().replace('.', ',');
        elements.propertyTax.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.propertyTax);
        elements.condoFees.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.condoFees);
        elements.insurancePno.value = new Intl.NumberFormat('fr-FR').format(DEFAULT_VALUES.insurancePno);
        elements.managementFeesRate.value = DEFAULT_VALUES.managementFeesRate.toString().replace('.', ',');
        elements.maintenanceRate.value = DEFAULT_VALUES.maintenanceRate.toString().replace('.', ',');

        calculate();
    });

    // Initialize tooltips (Bootstrap)
    if (typeof $ !== 'undefined' && $.fn.tooltip) {
        $('[data-toggle="tooltip"]').tooltip();
    }

    // =============================================
    // INITIALIZATION
    // =============================================

    // Format initial values
    document.querySelectorAll('.currency-input').forEach(input => {
        if (!input.readOnly) {
            formatInputValue(input, true);
        }
    });
    document.querySelectorAll('.percent-input').forEach(input => {
        formatInputValue(input, false);
    });

    // Run initial calculation
    calculate();
});

